import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Trash2, Upload, Lock, Unlock } from 'lucide-react';
import RoundManagement from './RoundManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsTab() {
  const [settings, setSettings] = useState({
    selection_window_open: true,
    selection_window_start: '',
    selection_window_end: '',
    moto_selection_window_open: true,
    landing_page_title: 'MSF RACING',
    landing_page_subtitle: 'FANTASY LEAGUE',
    landing_page_description: '',
    landing_page_image: '',
    landing_page_logo: '',
    landing_page_subtitle_logo: '',
    navbar_logo: '',
    login_page_title: 'MSF RACING',
    login_page_subtitle: 'Fantasy League',
    login_page_welcome_text: 'Welcome Back',
    login_page_register_text: 'Create Account',
    login_page_logo: '',
    dashboard_welcome_prefix: 'WELCOME BACK,',
    dashboard_subtitle: 'Your fantasy racing command center',
    dashboard_garage_title: 'MY GARAGE',
    dashboard_logo: '',
    team_selection_title: 'BUILD YOUR GARAGE',
    team_selection_subtitle: 'Select 2 drivers, 3 cars, and 1 tuner. Changes incur a 50-point penalty.',
    leaderboard_title: 'LEADERBOARD',
    leaderboard_subtitle: 'See how you stack up against the competition'
  });
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [pageLocks, setPageLocks] = useState({});

  const LOCKABLE_PAGES = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'leaderboard', label: 'Leaderboard' },
    { key: 'team_selection', label: 'Team Selection (Racing)' },
    { key: 'moto_selection', label: 'Team Selection (Moto)' },
    { key: 'workshop', label: 'Workshop' },
    { key: 'rules', label: 'Rules' },
    { key: 'results', label: 'Race Results' },
    { key: 'round_results', label: 'Standings' },
    { key: 'settings', label: 'Player Settings' },
  ];

  const handleLogoUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      toast.loading('Uploading file...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      
      const logoUrl = response.data.url;
      setSettings({ ...settings, [fieldName]: logoUrl });
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.dismiss();
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchPageLocks();
  }, []);

  const fetchPageLocks = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/page-locks`, { withCredentials: true });
      setPageLocks(res.data || {});
    } catch {}
  };

  const togglePageLock = async (pageKey, currentLock) => {
    const newLocked = !currentLock?.locked;
    let title = currentLock?.title || 'Page Locked';
    let message = currentLock?.message || '';
    
    if (newLocked && !message) {
      message = window.prompt('Enter the lock message players will see:', 'This page is currently unavailable. Please check back later.');
      if (message === null) return;
      title = window.prompt('Lock title (optional):', 'Page Locked') || 'Page Locked';
    }
    
    try {
      await axios.put(`${BACKEND_URL}/api/admin/page-locks/${pageKey}`, {
        locked: newLocked, title, message
      }, { withCredentials: true });
      toast.success(newLocked ? `Page locked` : `Page unlocked`);
      fetchPageLocks();
    } catch { toast.error('Failed to update lock'); }
  };

  const updateLockMessage = async (pageKey, field, value) => {
    const current = pageLocks[pageKey] || {};
    try {
      await axios.put(`${BACKEND_URL}/api/admin/page-locks/${pageKey}`, {
        ...current, [field]: value
      }, { withCredentials: true });
      fetchPageLocks();
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/system/settings`, { withCredentials: true });
      // Merge fetched settings with defaults to preserve all fields
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/system/settings`, settings, { withCredentials: true });
      setLastSaved(new Date().toLocaleTimeString());
      toast.success('✅ Settings saved! Changes are now live for all users.');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">System Settings</h2>
        {lastSaved && (
          <p className="text-sm text-secondary">Last saved: {lastSaved}</p>
        )}
      </div>

      <div className="space-y-8">
        {/* Selection Window Settings */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Selection Window Control</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="selection-window-open"
                data-testid="selection-window-toggle"
                checked={settings.selection_window_open}
                onChange={(e) => setSettings({ ...settings, selection_window_open: e.target.checked })}
                className="w-5 h-5 bg-zinc-950 border border-zinc-800 text-primary focus:ring-primary"
              />
              <label htmlFor="selection-window-open" className="text-white font-medium">
                Selection Window Open
              </label>
            </div>
            <p className="text-sm text-zinc-400">
              ⚠️ When unchecked, players with existing selections cannot make changes. New players can always make their first selection.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Window Start (Optional)</label>
                <input
                  type="datetime-local"
                  value={settings.selection_window_start || ''}
                  onChange={(e) => setSettings({ ...settings, selection_window_start: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Window End (Optional)</label>
                <input
                  type="datetime-local"
                  value={settings.selection_window_end || ''}
                  onChange={(e) => setSettings({ ...settings, selection_window_end: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Page Lock Controls */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-2 text-primary flex items-center gap-2">
            <Lock size={20} /> Page Locks
          </h3>
          <p className="text-sm text-zinc-400 mb-4">Lock pages to prevent player access. A custom message will be displayed.</p>
          <div className="space-y-3">
            {LOCKABLE_PAGES.map((page) => {
              const lock = pageLocks[page.key] || {};
              return (
                <div key={page.key} className={`border p-3 ${lock.locked ? 'border-red-700 bg-red-950/20' : 'border-zinc-800'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {lock.locked ? <Lock size={16} className="text-red-400" /> : <Unlock size={16} className="text-zinc-500" />}
                      <span className={`font-medium text-sm ${lock.locked ? 'text-red-300' : 'text-zinc-300'}`}>{page.label}</span>
                      {lock.locked && <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5">LOCKED</span>}
                    </div>
                    <button
                      onClick={() => togglePageLock(page.key, lock)}
                      data-testid={`lock-toggle-${page.key}`}
                      className={`px-3 py-1 text-xs font-bold transition-colors ${lock.locked ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-red-700 hover:bg-red-600 text-white'}`}
                    >
                      {lock.locked ? 'Unlock' : 'Lock'}
                    </button>
                  </div>
                  {lock.locked && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={lock.title || ''}
                        onChange={(e) => updateLockMessage(page.key, 'title', e.target.value)}
                        placeholder="Title"
                        className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs text-white focus:outline-none focus:border-primary"
                      />
                      <input
                        type="text"
                        value={lock.message || ''}
                        onChange={(e) => updateLockMessage(page.key, 'message', e.target.value)}
                        placeholder="Message to players"
                        className="col-span-2 bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs text-white focus:outline-none focus:border-primary"
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <button
              onClick={() => {
                const allLocked = LOCKABLE_PAGES.every(p => pageLocks[p.key]?.locked);
                const msg = allLocked ? null : window.prompt('Lock message for ALL pages:', 'The game is currently under maintenance. Please check back soon.');
                if (!allLocked && msg === null) return;
                LOCKABLE_PAGES.forEach(p => {
                  axios.put(`${BACKEND_URL}/api/admin/page-locks/${p.key}`, {
                    locked: !allLocked, title: 'Page Locked', message: msg || ''
                  }, { withCredentials: true });
                });
                setTimeout(fetchPageLocks, 500);
                toast.success(allLocked ? 'All pages unlocked' : 'All pages locked');
              }}
              data-testid="lock-all-toggle"
              className={`w-full py-2 text-sm font-bold transition-colors ${
                LOCKABLE_PAGES.every(p => pageLocks[p.key]?.locked)
                  ? 'bg-green-700 hover:bg-green-600 text-white'
                  : 'bg-red-700 hover:bg-red-600 text-white'
              }`}
            >
              {LOCKABLE_PAGES.every(p => pageLocks[p.key]?.locked) ? 'Unlock All Pages' : 'Lock All Pages'}
            </button>
          </div>
        </div>

        {/* Round Management */}
        <RoundManagement />

        {/* Game Reset Section */}
        <div className="border border-red-900/50 bg-red-950/20 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-red-500">Reset Game Data</h3>
          <p className="text-sm text-zinc-400 mb-4">
            ⚠️ This will clear all player team selections, leaderboard points, and selection history for both Racing and Moto leagues. 
            Players will need to reselect their teams. This action cannot be undone!
          </p>
          <button
            type="button"
            data-testid="reset-game-button"
            onClick={async () => {
              if (window.confirm('Are you sure you want to reset all game data? This will clear all team selections and leaderboard points. This action cannot be undone!')) {
                try {
                  toast.loading('Resetting game data...');
                  const response = await axios.post(`${BACKEND_URL}/api/admin/reset-game`, {}, { withCredentials: true });
                  toast.dismiss();
                  toast.success('Game reset successfully!');
                  if (process.env.NODE_ENV !== 'production') console.log('Reset details:', response.data);
                } catch (error) {
                  toast.dismiss();
                  toast.error(error.response?.data?.detail || 'Failed to reset game');
                }
              }
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold uppercase transition-colors"
          >
            Reset All Game Data
          </button>
        </div>

        {/* Leaderboard Recalculation */}
        <div className="border border-primary/50 bg-primary/10 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Recalculate Leaderboards</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Recalculate all player leaderboard points based on their selected drivers, cars, tuners (and riders, bikes for Moto).
            Use this after adding manual points to update player standings.
          </p>
          <button
            type="button"
            data-testid="recalculate-leaderboards-button"
            onClick={async () => {
              try {
                toast.loading('Recalculating leaderboards...');
                const response = await axios.post(`${BACKEND_URL}/api/admin/recalculate-leaderboards`, {}, { withCredentials: true });
                toast.dismiss();
                toast.success(`Leaderboards updated! Racing: ${response.data.racing_entries} entries, Moto: ${response.data.moto_entries} entries`);
              } catch (error) {
                toast.dismiss();
                toast.error(error.response?.data?.detail || 'Failed to recalculate');
              }
            }}
            className="bg-primary hover:bg-primary/80 text-white px-6 py-3 font-bold uppercase transition-colors"
          >
            Recalculate Now
          </button>
        </div>

        {/* Boost Reset Section */}
        <div className="border border-orange-900/50 bg-orange-950/20 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-orange-500">Round End — Reset Boosts</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Refund and reset all player boosts across both leagues. Players will need to re-purchase boosts before the next round.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="reset-boosts-all-button"
              onClick={async () => {
                if (window.confirm('Reset ALL boosts for both leagues? Credits will be refunded.')) {
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/admin/reset-boosts?league=all`, {}, { withCredentials: true });
                    toast.success(res.data.message);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to reset boosts');
                  }
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-bold uppercase transition-colors"
            >
              Reset All Boosts
            </button>
            <button
              type="button"
              data-testid="reset-boosts-racing-button"
              onClick={async () => {
                if (window.confirm('Reset Racing boosts only?')) {
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/admin/reset-boosts?league=racing`, {}, { withCredentials: true });
                    toast.success(res.data.message);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to reset boosts');
                  }
                }
              }}
              className="bg-primary/80 hover:bg-primary text-white px-6 py-3 font-bold uppercase transition-colors"
            >
              Reset Racing Only
            </button>
            <button
              type="button"
              data-testid="reset-boosts-moto-button"
              onClick={async () => {
                if (window.confirm('Reset Moto boosts only?')) {
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/admin/reset-boosts?league=moto`, {}, { withCredentials: true });
                    toast.success(res.data.message);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to reset boosts');
                  }
                }
              }}
              className="bg-secondary/80 hover:bg-secondary text-black px-6 py-3 font-bold uppercase transition-colors"
            >
              Reset Moto Only
            </button>
          </div>
        </div>

        {/* Recompute Snapshots (retroactive edit repair) */}
        <div className="border border-amber-900/50 bg-amber-950/20 p-6" data-testid="recompute-snapshots-panel">
          <h3 className="font-heading font-bold text-xl mb-3 text-amber-400">Recompute Round Snapshots</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Use this after retroactively editing points for a <strong>past</strong> round. It rebuilds every
            official snapshot's totals and per-round deltas from the current per-round entity points so the
            leaderboard math (<code className="text-zinc-300">R1 + R2_delta = R2_total</code>) reads correctly.
            Frozen boost bonuses are preserved.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['racing', 'moto'].map((league) => (
              <button
                key={league}
                type="button"
                data-testid={`recompute-snapshots-${league}`}
                onClick={async () => {
                  if (!window.confirm(`Recompute ALL ${league === 'racing' ? 'MSF Racing' : 'MotoMSF'} snapshots from current per-round points? Boost bonuses stay frozen.`)) return;
                  try {
                    const res = await axios.post(
                      `${BACKEND_URL}/api/admin/recompute-snapshots?league=${league}`,
                      {}, { withCredentials: true }
                    );
                    toast.success(res.data.message || `Recomputed ${res.data.snapshots_recomputed} snapshot(s)`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to recompute snapshots');
                  }
                }}
                className={`w-full py-3 font-bold uppercase text-sm transition-colors border ${league === 'racing' ? 'border-primary/50 hover:bg-primary/20 text-primary' : 'border-secondary/50 hover:bg-secondary/20 text-secondary'}`}
              >
                Recompute {league === 'racing' ? 'MSF Racing' : 'MotoMSF'}
              </button>
            ))}
          </div>
        </div>

        {/* Capture Round Results */}
        <div className="border border-blue-900/50 bg-blue-950/20 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-blue-400">Capture Round Results</h3>
          <p className="text-sm text-zinc-400 mb-4">
            After assigning points for a round, capture the results to show per-round player scores.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['racing', 'moto'].map((league) => (
              <div key={league} className="bg-zinc-900 border border-zinc-800 p-4">
                <p className={`font-heading font-bold mb-3 ${league === 'racing' ? 'text-primary' : 'text-secondary'}`}>{league === 'racing' ? 'MSF Racing' : 'MotoMSF'}</p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="Round #"
                    data-testid={`capture-round-number-${league}`}
                    id={`round-num-${league}`}
                    className="bg-zinc-800 border border-zinc-700 px-3 py-2 text-white text-sm w-24 focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Round name (optional)"
                    data-testid={`capture-round-name-${league}`}
                    id={`round-name-${league}`}
                    className="bg-zinc-800 border border-zinc-700 px-3 py-2 text-white text-sm flex-1 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  data-testid={`capture-round-${league}`}
                  onClick={async () => {
                    const num = document.getElementById(`round-num-${league}`).value;
                    const name = document.getElementById(`round-name-${league}`).value;
                    if (!num) { toast.error('Enter a round number'); return; }
                    try {
                      const res = await axios.post(
                        `${BACKEND_URL}/api/admin/capture-round?round_number=${num}&round_name=${encodeURIComponent(name)}&league=${league}`,
                        {}, { withCredentials: true }
                      );
                      toast.success(res.data.message);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to capture round');
                    }
                  }}
                  className={`w-full py-2 font-bold uppercase text-sm transition-colors ${league === 'racing' ? 'bg-primary text-white hover:opacity-80' : 'bg-secondary text-black hover:opacity-80'}`}
                >
                  Capture Round
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Landing Page Settings */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Landing Page (Home)</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Main Title Logo (replaces text)</label>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-4 py-2 cursor-pointer transition-colors">
                    <Upload size={18} />
                    <span>Upload Logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, 'landing_page_logo')}
                    />
                  </label>
                  {settings.landing_page_logo && (
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, landing_page_logo: '' })}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      Remove Logo
                    </button>
                  )}
                </div>
                {settings.landing_page_logo && (
                  <div className="bg-zinc-900 p-4 inline-block">
                    <img src={settings.landing_page_logo} alt="Main Logo Preview" className="h-20 object-contain" />
                  </div>
                )}
                <p className="text-xs text-zinc-500">If no logo is uploaded, the text title below will be shown</p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Page Title (fallback text)</label>
              <input
                type="text"
                data-testid="landing-title-input"
                value={settings.landing_page_title}
                onChange={(e) => setSettings({ ...settings, landing_page_title: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="MSF RACING"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subtitle Logo (replaces subtitle text)</label>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 bg-secondary hover:bg-secondary/80 text-black px-4 py-2 cursor-pointer transition-colors">
                    <Upload size={18} />
                    <span>Upload Subtitle Logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, 'landing_page_subtitle_logo')}
                    />
                  </label>
                  {settings.landing_page_subtitle_logo && (
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, landing_page_subtitle_logo: '' })}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      Remove Logo
                    </button>
                  )}
                </div>
                {settings.landing_page_subtitle_logo && (
                  <div className="bg-zinc-900 p-4 inline-block">
                    <img src={settings.landing_page_subtitle_logo} alt="Subtitle Logo Preview" className="h-12 object-contain" />
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subtitle (fallback text)</label>
              <input
                type="text"
                data-testid="landing-subtitle-input"
                value={settings.landing_page_subtitle}
                onChange={(e) => setSettings({ ...settings, landing_page_subtitle: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="FANTASY LEAGUE"
              />
            </div>
            
            {/* Navbar Logo */}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Navbar Logo (shown in navigation bar)</label>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-4 py-2 cursor-pointer transition-colors">
                    <Upload size={18} />
                    <span>Upload Navbar Logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, 'navbar_logo')}
                    />
                  </label>
                  {settings.navbar_logo && (
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, navbar_logo: '' })}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      Remove Logo
                    </button>
                  )}
                </div>
                {settings.navbar_logo && (
                  <div className="bg-zinc-900 p-4 inline-block">
                    <img src={settings.navbar_logo} alt="Navbar Logo Preview" className="h-10 object-contain" />
                  </div>
                )}
                <p className="text-xs text-zinc-500">Replaces the default "MSF RACING" text in the navigation bar</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
              <textarea
                data-testid="landing-description-input"
                value={settings.landing_page_description}
                onChange={(e) => setSettings({ ...settings, landing_page_description: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary p-4 text-white"
                rows="3"
                placeholder="Build your dream team. Compete for glory. Dominate the leaderboard."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Background Image</label>
              <div className="space-y-3">
                {/* File Upload */}
                <div className="flex items-center space-x-4">
                  <label 
                    className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-4 py-2 cursor-pointer transition-colors"
                    data-testid="upload-bg-image-btn"
                  >
                    <Upload size={18} />
                    <span>Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          toast.loading('Uploading image...');
                          const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
                            withCredentials: true,
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          toast.dismiss();
                          
                          const imageUrl = response.data.url;
                          setSettings({ ...settings, landing_page_image: imageUrl });
                          toast.success('Image uploaded successfully!');
                        } catch (error) {
                          toast.dismiss();
                          console.error('Upload failed:', error);
                          toast.error(error.response?.data?.detail || 'Failed to upload image');
                        }
                      }}
                    />
                  </label>
                  <span className="text-zinc-500 text-sm">or enter URL below</span>
                </div>
                
                {/* URL Input */}
                <input
                  type="url"
                  data-testid="landing-image-input"
                  value={settings.landing_page_image}
                  onChange={(e) => setSettings({ ...settings, landing_page_image: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                  placeholder="https://images.unsplash.com/..."
                />
                
                {/* Preview */}
                {settings.landing_page_image && (
                  <div className="relative">
                    <img src={settings.landing_page_image} alt="Preview" className="w-full h-40 object-cover border border-zinc-800" />
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, landing_page_image: '' })}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Login/Register Page */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Login & Register Page</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Page Title</label>
              <input
                type="text"
                value={settings.login_page_title}
                onChange={(e) => setSettings({ ...settings, login_page_title: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="MSF RACING"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subtitle</label>
              <input
                type="text"
                value={settings.login_page_subtitle}
                onChange={(e) => setSettings({ ...settings, login_page_subtitle: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="Fantasy League"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Welcome Back Text (Login)</label>
              <input
                type="text"
                value={settings.login_page_welcome_text}
                onChange={(e) => setSettings({ ...settings, login_page_welcome_text: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="Welcome Back"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Register Text</label>
              <input
                type="text"
                value={settings.login_page_register_text}
                onChange={(e) => setSettings({ ...settings, login_page_register_text: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="Create Account"
              />
            </div>
          </div>
        </div>

        {/* Dashboard Page */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Dashboard Page</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Welcome Prefix</label>
              <input
                type="text"
                value={settings.dashboard_welcome_prefix}
                onChange={(e) => setSettings({ ...settings, dashboard_welcome_prefix: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="WELCOME BACK,"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subtitle</label>
              <input
                type="text"
                value={settings.dashboard_subtitle}
                onChange={(e) => setSettings({ ...settings, dashboard_subtitle: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="Your fantasy racing command center"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Garage Section Title</label>
              <input
                type="text"
                value={settings.dashboard_garage_title}
                onChange={(e) => setSettings({ ...settings, dashboard_garage_title: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="MY GARAGE"
              />
            </div>
          </div>
        </div>

        {/* Team Selection Page */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Team Selection Page</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Page Title</label>
              <input
                type="text"
                value={settings.team_selection_title}
                onChange={(e) => setSettings({ ...settings, team_selection_title: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="BUILD YOUR GARAGE"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subtitle</label>
              <input
                type="text"
                value={settings.team_selection_subtitle}
                onChange={(e) => setSettings({ ...settings, team_selection_subtitle: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="Select 2 drivers, 3 cars, and 1 tuner."
              />
            </div>
          </div>
        </div>

        {/* Leaderboard Page */}
        <div className="border border-zinc-700 p-6">
          <h3 className="font-heading font-bold text-xl mb-4 text-primary">Leaderboard Page</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Page Title</label>
              <input
                type="text"
                value={settings.leaderboard_title}
                onChange={(e) => setSettings({ ...settings, leaderboard_title: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="LEADERBOARD"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subtitle</label>
              <input
                type="text"
                value={settings.leaderboard_subtitle}
                onChange={(e) => setSettings({ ...settings, leaderboard_subtitle: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                placeholder="See how you stack up against the competition"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={loading}
          data-testid="save-settings-button"
          className="w-full btn-skew bg-primary text-white py-4 font-heading font-bold text-xl uppercase tracking-wider disabled:opacity-50 neon-glow-hover"
        >
          <span>{loading ? 'Saving...' : '💾 Save All Changes (Live Update)'}</span>
        </button>
        <p className="text-center text-sm text-zinc-500">Changes take effect immediately for all users after saving</p>
      </div>
    </div>
  );
}
